import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Power, PowerOff, ChevronRight } from "lucide-react";
import { ScreenTitle } from "@/components/ScreenTitle";

export default function PoliticsCategoriesPage() {
  const [cats, setCats] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#2a5788");

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/politics/categories"),
        api.get("/politics/stats"),
      ]);
      setCats(c.data);
      setStats(s.data);
    } catch (e) { /* interceptor */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) { toast.error("Nom requis"); return; }
    setCreating(true);
    try {
      await api.post("/politics/categories", { name: newName.trim(), color: newColor });
      toast.success("Catégorie créée");
      setNewName(""); setNewColor("#2a5788");
      load();
    } catch (e) { toast.error("Création impossible"); }
    finally { setCreating(false); }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/politics/categories/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error("Impossible"); }
  };

  const per = stats?.per_category || {};

  return (
    <div data-testid="politics-categories-page">
      <ScreenTitle eyebrow={`${cats.length} catégorie(s)`} title="Catégories" />

      <div className="px-5 pb-5">
        <div className="border border-[var(--border-default)] p-4 space-y-3 bg-white">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Nouvelle catégorie
          </div>
          <input
            type="text" placeholder="Nom (ex. Sport)" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="new-cat-name"
            className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black"
          />
          <div className="flex items-center gap-3">
            <label className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Couleur</label>
            <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
                   data-testid="new-cat-color"
                   className="w-16 h-10 border border-[var(--border-default)]" />
            <span className="font-mono text-xs">{newColor}</span>
          </div>
          <button onClick={create} disabled={creating} data-testid="new-cat-save"
            className="w-full min-h-[44px] flex items-center justify-center gap-2 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60">
            {creating ? "…" : "Créer"}
          </button>
        </div>
      </div>

      {loading && <p className="px-5 text-sm text-[var(--text-muted)]">Chargement…</p>}

      <ul className="divide-y divide-[var(--border-default)]" data-testid="categories-list">
        {cats.map((c) => {
          const p = per[c.id] || {};
          return (
            <li key={c.id} data-testid="category-item" className="px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="w-4 h-4 flex-shrink-0" style={{ background: c.color }} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-lg leading-tight">{c.name}</h3>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-0.5 flex flex-wrap gap-2">
                    <span>{c.active ? "actif" : "inactif"}</span>
                    <span>·</span>
                    <span>{Math.round(c.poll_interval_seconds / 60)} min</span>
                    <span>·</span>
                    <span>{p.new ?? 0} nouvelles</span>
                    {(p.urgent ?? 0) > 0 && <>
                      <span>·</span>
                      <span className="text-[var(--accent-strong)]">{p.urgent}🔴</span>
                    </>}
                  </div>
                </div>
                <button onClick={() => toggle(c)} data-testid="cat-toggle"
                  className="min-h-[36px] px-2 border border-[var(--border-default)] hover:border-black flex items-center text-[10px] font-mono uppercase">
                  {c.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                </button>
                <Link to={`/veille/categories/${c.id}`}
                      data-testid="cat-open"
                      className="min-h-[36px] px-3 flex items-center gap-1 border border-black bg-white hover:bg-black hover:text-white text-[11px] font-mono uppercase tracking-widest">
                  Ouvrir <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
